import type { ExitPlacementSliceRef, SimulateLegs, SimulatedLeg } from "../types/managementExplorer";
import type { PolicyTreeV2ManagementAction, PolicyTreeV2Rule } from "../types/tradePolicy";
import type { ReportPick } from "./reportPickListStorage";
import { isExitPlacementPick, simulationRuleFromExitPlacementPick } from "./exitPlacementPicks";

export type ExitPlacementLegAvailability = {
  hasSl: boolean;
  hasTp: boolean;
};

const SL_ACTION = "sl_distance_override";
const TP_ACTION = "tp_distance_override";

export function actionTypesFromSimulationActions(
  actions: PolicyTreeV2ManagementAction[] | null | undefined
): string[] {
  return (actions ?? [])
    .map((a) => String(a?.type ?? "").trim())
    .filter((t) => t === SL_ACTION || t === TP_ACTION);
}

export function legAvailabilityFromActions(
  actions: PolicyTreeV2ManagementAction[] | null | undefined
): ExitPlacementLegAvailability {
  const types = actionTypesFromSimulationActions(actions);
  return {
    hasSl: types.includes(SL_ACTION),
    hasTp: types.includes(TP_ACTION),
  };
}

export function mergeLegAvailability(
  ...items: ExitPlacementLegAvailability[]
): ExitPlacementLegAvailability {
  return {
    hasSl: items.some((a) => a.hasSl),
    hasTp: items.some((a) => a.hasTp),
  };
}

export function legAvailabilityFromExitPlacementPick(pick: ReportPick): ExitPlacementLegAvailability {
  const rule = simulationRuleFromExitPlacementPick(pick);
  return legAvailabilityFromActions(rule?.actions);
}

export function legAvailabilityFromExitPlacementPicks(picks: ReportPick[]): ExitPlacementLegAvailability {
  return mergeLegAvailability(
    ...picks.filter(isExitPlacementPick).map(legAvailabilityFromExitPlacementPick)
  );
}

export function legAvailabilityFromPolicyRules(
  rules: PolicyTreeV2Rule[] | null | undefined
): ExitPlacementLegAvailability {
  return mergeLegAvailability(...(rules ?? []).map((r) => legAvailabilityFromActions(r.actions)));
}

/** Default simulate_legs from which SL/TP actions exist in the policy tree or picks. */
export function defaultSimulateLegs(availability: ExitPlacementLegAvailability): SimulateLegs {
  if (availability.hasSl && availability.hasTp) return "both";
  if (availability.hasSl) return "sl_only";
  if (availability.hasTp) return "tp_only";
  return "both";
}

/** User-facing Action label for Exit Placement (SL / TP / Both — not Remove/Keep). */
export function formatExitPlacementLegsActionLabel(
  availability: ExitPlacementLegAvailability
): string {
  if (availability.hasSl && availability.hasTp) return "Both";
  if (availability.hasSl) return "SL";
  if (availability.hasTp) return "TP";
  return "Exit placement";
}

/** Compact kind badge for overlap / chips: SL, TP, or SL TP. */
export function formatExitPlacementLegsKindBadge(
  availability: ExitPlacementLegAvailability
): string {
  if (availability.hasSl && availability.hasTp) return "SL TP";
  if (availability.hasSl) return "SL";
  if (availability.hasTp) return "TP";
  return "Exit placement";
}

export function isSimulateLegAllowed(
  leg: SimulateLegs,
  availability: ExitPlacementLegAvailability
): boolean {
  if (leg === "sl_only") return availability.hasSl;
  if (leg === "tp_only") return availability.hasTp;
  return availability.hasSl && availability.hasTp;
}

export function clampSimulateLegs(
  current: SimulateLegs,
  availability: ExitPlacementLegAvailability
): SimulateLegs {
  if (isSimulateLegAllowed(current, availability)) return current;
  return defaultSimulateLegs(availability);
}

export type SimulateLegOption = {
  value: SimulateLegs;
  label: string;
  tooltip: string;
  disabled: boolean;
  hidden: boolean;
};

export function simulateLegOptions(availability: ExitPlacementLegAvailability): SimulateLegOption[] {
  const { hasSl, hasTp } = availability;
  const bothLegs = hasSl && hasTp;
  return [
    {
      value: "sl_only",
      label: "Stop only",
      tooltip: "Replay recommended stop distance; keep your original targets.",
      disabled: !hasSl,
      hidden: !hasSl,
    },
    {
      value: "tp_only",
      label: "Target only",
      tooltip: "Replay giveback-capture target; keep your original stops.",
      disabled: !hasTp,
      hidden: !hasTp,
    },
    {
      value: "both",
      label: "Both",
      tooltip: "Apply stop and target together (winners may exit earlier on target).",
      disabled: !bothLegs,
      hidden: !bothLegs,
    },
  ];
}

export function formatSimulatedLegsBadge(legs: SimulatedLeg[] | null | undefined): string | null {
  if (!legs?.length) return null;
  const hasSl = legs.includes("sl");
  const hasTp = legs.includes("tp");
  if (hasSl && hasTp) return "Simulated: Stop + Target";
  if (hasSl) return "Simulated: Stop";
  if (hasTp) return "Simulated: Target";
  return null;
}

export function exitPlacementSliceRecapLabels(
  slices: ExitPlacementSliceRef[] | null | undefined
): string[] {
  return (slices ?? [])
    .map((s) => s.display_label?.trim() || s.segment_label?.trim() || "")
    .filter(Boolean);
}

export function normalizeSimulatedLegs(raw: unknown): SimulatedLeg[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const legs = raw
    .map((v) => String(v ?? "").trim().toLowerCase())
    .filter((v): v is SimulatedLeg => v === "sl" || v === "tp");
  return legs.length ? legs : undefined;
}

export function normalizeSimulateLegs(raw: unknown): SimulateLegs | undefined {
  const v = String(raw ?? "").trim().toLowerCase();
  if (v === "both" || v === "sl_only" || v === "tp_only") return v;
  return undefined;
}

/** Whether to draw simulated SL/TP on chart for the selected replay legs. */
export function shouldDrawSimulatedSl(
  simulatedLegs: SimulatedLeg[] | null | undefined
): boolean {
  return !simulatedLegs?.length || simulatedLegs.includes("sl");
}

export function shouldDrawSimulatedTp(
  simulatedLegs: SimulatedLeg[] | null | undefined
): boolean {
  return !simulatedLegs?.length || simulatedLegs.includes("tp");
}
